/**
 * #445-ws2 — the SCORED-anomaly detail badge for the OPERATOR-ASSESSED Response Level overlay
 * (flood + ice-storm). The READ-ONLY sibling of the incident badge (IncidentManagementSection):
 * it surfaces the OPERATIVE level (effective = operator ?? derived) ALONGSIDE the annotated
 * system-derived + the IC attribution/reason + the de-escalation divergence guard — never a
 * silent overwrite of the derived. The Response Level is SET on the owning incident (the work
 * surface), never here; this only surfaces the pin so the anomaly panel reads the same truth
 * the incident badge, the map disc, and the queue row do.
 *
 * #613 Threat/Response split — the two axes are honest here:
 *   • WITH an operator pin, this anomaly is OWNED by an incident whose IC assessed a §5
 *     Response Level → the operative badge is the RESPONSE axis.
 *   • WITHOUT a pin (a non-owned anomaly), the effective level IS the machine-derived level →
 *     the operative badge is the THREAT axis (calling it "Response Level" would reproduce the
 *     exact false picture the split kills). The annotated system-derived line is ALWAYS the
 *     machine THREAT axis; the assessed pin it is compared against is the §5 RESPONSE axis.
 */
import { effectiveResponseLevel, derivedExceedsOperator } from '@tsr/shared-types';
import { ResponseLevelBadge } from './ResponseLevelBadge';
import { THREAT_LEVEL_META, isResponseLevel } from './styles/responseLevel';

export function AnomalyResponseLevelBadge({
  derived,
  operatorSet,
  assessedBy,
  reason,
  testidPrefix,
  extra,
}: {
  /** The system-derived Response Level the anomaly always carries (1..4). */
  derived: number | null | undefined;
  /** The owning IC's operator-assessed level, or null when not owned / no pin. */
  operatorSet: number | null | undefined;
  /** The assessing Incident Commander (the "assessed by <IC>" annotation). */
  assessedBy: string | null | undefined;
  /** The IC's assessment reason, shown when a pin exists. */
  reason: string | null | undefined;
  /** Distinct data-testid namespace so flood + ice bodies target their own badges. */
  testidPrefix: string;
  /** Inline chips (acknowledged / gauge-sparse) rendered beside the effective badge. */
  extra?: React.ReactNode;
}): JSX.Element {
  const hasPin = operatorSet != null;
  const effective = effectiveResponseLevel(operatorSet, derived);
  const divergence = derivedExceedsOperator(operatorSet, derived);
  return (
    <div className="flex flex-col gap-1" data-testid={testidPrefix}>
      {/* Operative (effective) level = the IC pin over the derived, shown with the status chips.
          #613 — RESPONSE axis when owned (a pin exists); THREAT axis when un-owned (derived-only). */}
      <div className="flex flex-wrap items-center gap-2">
        <ResponseLevelBadge
          level={effective}
          testid={`${testidPrefix}-effective`}
          axis={hasPin ? 'response' : 'threat'}
        />
        {hasPin && (
          <span className="text-[11px] text-slate-400" data-testid={`${testidPrefix}-attribution`}>
            assessed by {assessedBy ?? 'the Incident Commander'}
          </span>
        )}
        {extra}
      </div>
      {hasPin && reason && (
        <p className="text-[11px] italic text-slate-400" data-testid={`${testidPrefix}-reason`}>
          “{reason}”
        </p>
      )}
      {/* The SYSTEM-DERIVED level — ALWAYS annotated when a pin exists (never overwritten).
          #613 — a system DERIVES a THREAT, not a §5 response scale. */}
      {hasPin && (
        <p className="text-[11px] text-slate-500" data-testid={`${testidPrefix}-derived`}>
          {isResponseLevel(derived)
            ? `system-derived Threat Level ${derived} · ${THREAT_LEVEL_META[derived].name}`
            : 'no system-derived Threat Level'}
        </p>
      )}
      {/* DE-ESCALATION GUARD — a worsening derived the pin now sits below MUST surface.
          #613 — the machine THREAT now exceeds the IC's assessed §5 RESPONSE level. */}
      {divergence && isResponseLevel(derived) && isResponseLevel(operatorSet) && (
        <p
          className="rounded border border-red-900/60 bg-red-950/40 px-2 py-1 text-[11px] font-semibold text-red-300"
          data-testid={`${testidPrefix}-divergence`}
        >
          ⚠ System-derived Threat Level {derived} now exceeds the assessed Response Level{' '}
          {operatorSet} — re-assess.
        </p>
      )}
    </div>
  );
}
